import { useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { User, Mail, Phone, Lock, AlertCircle } from "lucide-react";
import { useCustomerAuth } from "../lib/CustomerAuthContext";
import { cn } from "../lib/utils";

export default function AuthModal({ open, onClose, onSuccess }) {
  const { login, register } = useCustomerAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const resetAndClose = () => {
    setForm({ name: "", email: "", phone: "", password: "" });
    setError("");
    setMode("login");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = mode === "login" ? await login(form) : await register(form);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setForm({ name: "", email: "", phone: "", password: "" });
    onSuccess?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && resetAndClose()}>
      <DialogContent className="max-w-md w-[92vw] sm:w-full p-0 bg-white border-0 rounded-3xl gap-0 overflow-hidden" data-testid="auth-modal">
        <DialogTitle className="sr-only">{mode === "login" ? "Giriş Yap" : "Kayıt Ol"}</DialogTitle>
        <DialogDescription className="sr-only">Hesabınıza giriş yapın veya yeni hesap oluşturun.</DialogDescription>

        <div className="flex border-b border-[#EAEAEA]" data-testid="auth-modal-tabs">
          <button
            type="button"
            onClick={() => { setMode("login"); setError(""); }}
            className={cn(
              "flex-1 py-4 text-sm font-medium tracking-wide transition-colors",
              mode === "login" ? "text-[#222222] border-b-2 border-[#A85C34]" : "text-[#999999] hover:text-[#555555]"
            )}
            data-testid="auth-tab-login"
          >
            Giriş Yap
          </button>
          <button
            type="button"
            onClick={() => { setMode("register"); setError(""); }}
            className={cn(
              "flex-1 py-4 text-sm font-medium tracking-wide transition-colors",
              mode === "register" ? "text-[#222222] border-b-2 border-[#A85C34]" : "text-[#999999] hover:text-[#555555]"
            )}
            data-testid="auth-tab-register"
          >
            Kayıt Ol
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-7 space-y-4">
          <div className="text-center mb-1">
            <h3 className="font-serif text-2xl text-[#222222]">
              {mode === "login" ? "Tekrar Hoş Geldiniz" : "Hesap Oluşturun"}
            </h3>
            <p className="text-sm text-[#888888] mt-1">
              {mode === "login" ? "Siparişlerinizi ve adreslerinizi görüntüleyin." : "Sonraki siparişleriniz daha hızlı olsun."}
            </p>
          </div>

          {mode === "register" && (
            <div className="relative">
              <User className="w-4 h-4 text-[#888888] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <Input
                required
                value={form.name}
                onChange={handleChange("name")}
                placeholder="Ad Soyad"
                className="h-12 pl-10 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
                data-testid="auth-name-input"
              />
            </div>
          )}

          <div className="relative">
            <Mail className="w-4 h-4 text-[#888888] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input
              required
              type="email"
              value={form.email}
              onChange={handleChange("email")}
              placeholder="E-posta adresiniz"
              className="h-12 pl-10 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
              data-testid="auth-email-input"
            />
          </div>

          {mode === "register" && (
            <div className="relative">
              <Phone className="w-4 h-4 text-[#888888] absolute left-3.5 top-1/2 -translate-y-1/2" />
              <Input
                type="tel"
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="Telefon (isteğe bağlı)"
                className="h-12 pl-10 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
                data-testid="auth-phone-input"
              />
            </div>
          )}

          <div className="relative">
            <Lock className="w-4 h-4 text-[#888888] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <Input
              required
              type="password"
              value={form.password}
              onChange={handleChange("password")}
              placeholder="Şifre"
              minLength={6}
              className="h-12 pl-10 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
              data-testid="auth-password-input"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs" data-testid="auth-error">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full px-6 py-3.5 font-medium transition-colors disabled:opacity-60"
            data-testid="auth-submit"
          >
            {submitting ? "Lütfen bekleyin..." : mode === "login" ? "Giriş Yap" : "Hesap Oluştur"}
          </button>

          <button
            type="button"
            onClick={resetAndClose}
            className="w-full text-center text-sm text-[#888888] hover:text-[#222222] transition-colors"
            data-testid="auth-guest-continue"
          >
            Hesap açmadan misafir olarak devam et
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
